import React from 'react';
import { Gauge, CheckCircle2, EyeOff, Eye, Lightbulb } from 'lucide-react';
import ReliabilityBadge from './ReliabilityBadge';
import ReliabilityHistoryChart from './ReliabilityHistoryChart';
import { i18n, useT } from '../../services/i18n';

const Bar = ({ label, value, weight, hint, testId, t }) => (
  <div data-testid={testId}>
    <div className="flex items-center justify-between text-xs text-gray-700"><span>{label} <span className="text-gray-400">({t('ps.ofScore', { n: weight })})</span></span><strong>{Math.round(value)}/100</strong></div>
    <div className="mt-1 h-2 w-full rounded-full bg-gray-200 overflow-hidden"><div className={`h-2 rounded-full transition-all ${value >= 80 ? 'bg-emerald-500' : value >= 50 ? 'bg-amber-400' : 'bg-red-500'}`} style={{ width: `${Math.max(3, Math.min(100, value))}%` }} /></div>
    <p className="mt-0.5 text-[11px] text-gray-500">{hint}</p>
  </div>
);

// Partner space: live reliability score, its 3 components and the points to improve
const PartnerReliabilityCard = ({ data }) => {
  const { t } = useT();
  if (!data) return null;
  const r = data.recent;
  const st = data.stats || {};
  const h = st.avg_confirmation_hours;
  const delayHint = h == null ? t('ps.noDelay') : h < 1 ? t('ps.delayUnderHour') : t('ps.delayAvg', { d: h < 48 ? `${Math.round(h)} h` : `${(h / 24).toFixed(1)} ${t('ps.dayShort')}` });
  return (
    <section className="bg-white rounded-xl shadow-sm p-6 mb-6" data-testid="partner-reliability">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-base md:text-lg font-bold text-gray-900 flex items-center gap-2"><Gauge size={18} className="text-orange-500" /> {t('ps.relTitle')} <span className="text-xs font-normal text-gray-500">({t('ps.relWindow', { n: data.window_days })})</span></h2>
          <p className="text-xs text-gray-500 mt-1">{t('ps.relIntro', { n: data.min_sample })}</p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <ReliabilityBadge reliability={r} testId="partner-reliability-badge" />
          {data.visible_at_checkout
            ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700" data-testid="partner-visible"><Eye size={12} /> {t('ps.visible')}</span>
            : <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-700" data-testid="partner-hidden"><EyeOff size={12} /> {t('ps.hidden')}{data.hidden_since ? ` ${t('ps.since', { d: new Date(data.hidden_since).toLocaleDateString(i18n.locale()) })}` : ''}</span>}
        </div>
      </div>
      {!r ? <p className="text-sm text-gray-400" data-testid="partner-reliability-empty">{t('ps.relEmpty')}</p> : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Bar t={t} label={t('ps.barConfirmed')} value={r.components.confirmation} weight={40} hint={`${t('ps.confirmedOf', { c: st.confirmed ?? 0, o: st.orders ?? 0 })}${st.pending ? ` · ${t('ps.pendingN', { n: st.pending })}` : ''}`} testId="partner-bar-confirmation" />
            <Bar t={t} label={t('ps.barDelay')} value={r.components.delay} weight={35} hint={delayHint} testId="partner-bar-delay" />
            <Bar t={t} label={t('ps.barKept')} value={r.components.reassign} weight={25} hint={r.reassigned_away ? t('ps.reassignedN', { n: r.reassigned_away }) : t('ps.noReassigned')} testId="partner-bar-reassign" />
          </div>
          <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50/60 p-3" data-testid="partner-points">
            <p className="text-sm font-semibold text-amber-900 inline-flex items-center gap-1.5"><Lightbulb size={15} /> {data.points.length && r.label !== 'Excellent' ? t('ps.pointsTitle') : t('ps.keepGoing')}</p>
            {r.label === 'Excellent'
              ? <p className="text-xs text-amber-900 mt-1 inline-flex items-center gap-1"><CheckCircle2 size={12} /> {t('ps.excellent')}</p>
              : <ul className="mt-1 space-y-1 text-xs text-amber-900 list-disc pl-4">{data.points.map((p, i) => <li key={i} data-testid={`partner-point-${i}`}>{p}</li>)}</ul>}
          </div>
          <div className="mt-4 rounded-lg border border-gray-200 p-3"><ReliabilityHistoryChart history={data.history || []} /></div>
          {st.count > 0 && <p className="mt-3 text-[11px] text-gray-500">{t('ps.ratingLine', { avg: String(st.avg).replace('.', ','), count: st.count, upcoming: st.upcoming ?? 0 })}</p>}
        </>
      )}
    </section>
  );
};

export default PartnerReliabilityCard;
